// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module CloudflareKVAnalyticsStore
 * @package @geenius/analytics/cloudflareKV
 * @description Cloudflare KV analytics provider for edge-friendly event,
 * page-view, consent, counter, and aggregate snapshot storage.
 */

import type {
  AnalyticsEvent,
  ConsentSettings,
  PageView,
  UserTraits,
} from "@geenius/analytics";
import { AdapterUnsupportedError } from "@geenius/analytics";

/**
 * Minimal KV list response shape consumed by the analytics store.
 */
export interface KVListResult {
  keys: Array<{ name: string }>;
  list_complete: boolean;
  cursor?: string;
}

/**
 * Cloudflare KV-compatible namespace contract required by the analytics store.
 */
export interface AnalyticsKVNamespace {
  get(key: string): Promise<string | null>;
  put(
    key: string,
    value: string,
    options?: { expirationTtl?: number },
  ): Promise<void>;
  delete(key: string): Promise<void>;
  list(options?: {
    prefix?: string;
    cursor?: string;
    limit?: number;
  }): Promise<KVListResult>;
}

/**
 * Options used to bind analytics storage to a KV namespace.
 */
export interface CloudflareKVAnalyticsStoreOptions {
  namespace: AnalyticsKVNamespace;
  keyPrefix?: string;
  /** Legacy short-lived record TTL applied only to events, page views, and sessions. */
  ttlSeconds?: number;
  /** Optional TTL for individual tracked event records. */
  eventsTtlSeconds?: number;
  /** Optional TTL for individual page-view records. */
  pageViewsTtlSeconds?: number;
  /** Optional TTL for session activity records. */
  sessionsTtlSeconds?: number;
  /** Optional TTL for counter records; counters are durable by default. */
  countersTtlSeconds?: number;
  /** Optional TTL for page-view aggregate records; aggregates are durable by default. */
  pageViewAggregatesTtlSeconds?: number;
  /** Optional TTL for generic aggregate snapshots; snapshots are durable by default. */
  aggregateSnapshotsTtlSeconds?: number;
}

/**
 * TTL-backed analytics session record stored in KV.
 */
export interface CloudflareKVSessionRecord {
  sessionId: string;
  userId?: string;
  startedAt: number;
  lastSeenAt: number;
  metadata?: Record<string, unknown>;
}

/**
 * Counter value stored as a single KV key.
 */
export interface CloudflareKVCounterRecord {
  name: string;
  value: number;
  updatedAt: number;
}

/**
 * Page-view aggregate snapshot maintained per URL.
 */
export interface CloudflareKVPageViewAggregate {
  url: string;
  views: number;
  uniqueVisitors: number;
  lastViewedAt: number;
}

interface StoredPageViewAggregate extends CloudflareKVPageViewAggregate {
  visitorIds: string[];
}

/**
 * Generic aggregate snapshot persisted for dashboard/realtime consumers.
 */
export interface CloudflareKVAggregateSnapshot<TData = unknown> {
  id: string;
  kind: "dashboard" | "realtime" | "pageviews" | "custom";
  data: TData;
  generatedAt: number;
}

interface AggregateSnapshotIndexEntry {
  id: string;
  kind: CloudflareKVAggregateSnapshot["kind"];
}

/**
 * Public Cloudflare KV analytics store contract for edge-friendly event storage.
 */
export interface CloudflareKVAnalyticsStore {
  trackEvent(event: AnalyticsEvent): Promise<AnalyticsEvent>;
  trackPageView(pageView: PageView): Promise<PageView>;
  identifyUser(userId: string, traits?: UserTraits): Promise<void>;
  listEvents(limit?: number): Promise<AnalyticsEvent[]>;
  getEvent(id: string): Promise<AnalyticsEvent | null>;
  deleteEvent(id: string): Promise<boolean>;
  putConsentState(userId: string, consent: ConsentSettings): Promise<void>;
  getConsentState(userId: string): Promise<ConsentSettings | null>;
  upsertSession(
    session: CloudflareKVSessionRecord,
  ): Promise<CloudflareKVSessionRecord>;
  getSession(sessionId: string): Promise<CloudflareKVSessionRecord | null>;
  incrementCounter(
    name: string,
    delta?: number,
  ): Promise<CloudflareKVCounterRecord>;
  getCounter(name: string): Promise<CloudflareKVCounterRecord | null>;
  getPageViewAggregate(
    url: string,
  ): Promise<CloudflareKVPageViewAggregate | null>;
  listPageViewAggregates(
    limit?: number,
  ): Promise<CloudflareKVPageViewAggregate[]>;
  putAggregateSnapshot<TData>(
    snapshot: CloudflareKVAggregateSnapshot<TData>,
  ): Promise<CloudflareKVAggregateSnapshot<TData>>;
  getAggregateSnapshot<TData = unknown>(
    id: string,
  ): Promise<CloudflareKVAggregateSnapshot<TData> | null>;
  listAggregateSnapshots(
    kind?: CloudflareKVAggregateSnapshot["kind"],
    limit?: number,
  ): Promise<Array<CloudflareKVAggregateSnapshot<unknown>>>;
  deleteUserData(userId: string): Promise<void>;
  unsupportedRelationalQuery(operation: string): never;
}

/**
 * Creates an in-memory KV namespace compatible with the Cloudflare KV provider.
 *
 * @returns A deterministic KV namespace useful for tests and local demos.
 */
export function createMemoryKVNamespace(): AnalyticsKVNamespace {
  const values = new Map<string, string>();
  const expirations = new Map<string, number>();
  const purgeExpired = (key: string): void => {
    const expiresAt = expirations.get(key);
    if (expiresAt !== undefined && expiresAt <= Date.now()) {
      values.delete(key);
      expirations.delete(key);
    }
  };

  return {
    async get(key) {
      purgeExpired(key);
      return values.get(key) ?? null;
    },
    async put(key, value, options) {
      values.set(key, value);
      if (options?.expirationTtl) {
        expirations.set(key, Date.now() + options.expirationTtl * 1000);
      } else {
        expirations.delete(key);
      }
    },
    async delete(key) {
      values.delete(key);
      expirations.delete(key);
    },
    async list(options = {}) {
      const prefix = options.prefix ?? "";
      const limit = options.limit ?? 1000;
      const keys = [...values.keys()]
        .filter((key) => {
          purgeExpired(key);
          return values.has(key);
        })
        .filter((key) => key.startsWith(prefix))
        .sort((left, right) => left.localeCompare(right))
        .slice(0, limit)
        .map((name) => ({ name }));
      return { keys, list_complete: true };
    },
  };
}

/**
 * Creates an analytics store backed by a Cloudflare KV-compatible namespace.
 *
 * @param options KV namespace, key prefix, and optional TTL settings.
 * @returns A Cloudflare KV analytics store implementation.
 */
export function createCloudflareKVAnalyticsStore(
  options: CloudflareKVAnalyticsStoreOptions,
): CloudflareKVAnalyticsStore {
  const prefix = options.keyPrefix ?? "analytics";
  const eventsTtlSeconds = options.eventsTtlSeconds ?? options.ttlSeconds;
  const pageViewsTtlSeconds =
    options.pageViewsTtlSeconds ?? options.ttlSeconds;
  const sessionsTtlSeconds = options.sessionsTtlSeconds ?? options.ttlSeconds;
  const keyFor = (key: string): string => `${prefix}:${key}`;
  const ttlOptions = (
    ttlSeconds: number | undefined,
  ): { expirationTtl?: number } | undefined => {
    return ttlSeconds === undefined ? undefined : { expirationTtl: ttlSeconds };
  };
  const putJson = async (
    key: string,
    value: unknown,
    ttlSeconds?: number,
  ): Promise<void> => {
    await options.namespace.put(
      keyFor(key),
      JSON.stringify(value),
      ttlOptions(ttlSeconds),
    );
  };
  const getJson = async <T>(key: string): Promise<T | null> => {
    const value = await options.namespace.get(keyFor(key));
    return value ? (JSON.parse(value) as T) : null;
  };
  const listJson = async <T>(keyPrefix: string, limit = 100): Promise<T[]> => {
    const result = await options.namespace.list({
      prefix: keyFor(keyPrefix),
      limit,
    });
    const records: T[] = [];
    for (const key of result.keys) {
      const value = await options.namespace.get(key.name);
      if (value) records.push(JSON.parse(value) as T);
    }
    return records;
  };
  const touchSession = async (input: {
    sessionId: string;
    userId?: string;
    timestamp: number;
  }): Promise<void> => {
    const existing = await getJson<CloudflareKVSessionRecord>(
      `sessions:${input.sessionId}`,
    );
    await putJson(
      `sessions:${input.sessionId}`,
      {
        sessionId: input.sessionId,
        userId: input.userId ?? existing?.userId,
        startedAt: existing?.startedAt ?? input.timestamp,
        lastSeenAt: Math.max(
          existing?.lastSeenAt ?? input.timestamp,
          input.timestamp,
        ),
        metadata: existing?.metadata,
      } satisfies CloudflareKVSessionRecord,
      sessionsTtlSeconds,
    );
  };
  const publicPageAggregate = (
    aggregate: StoredPageViewAggregate,
  ): CloudflareKVPageViewAggregate => ({
    url: aggregate.url,
    views: aggregate.views,
    uniqueVisitors: aggregate.uniqueVisitors,
    lastViewedAt: aggregate.lastViewedAt,
  });

  return {
    async trackEvent(event) {
      await putJson(`events:${event.id}`, event, eventsTtlSeconds);
      await touchSession({
        sessionId: event.sessionId,
        userId: event.userId,
        timestamp: event.timestamp,
      });
      await this.incrementCounter(`events:${event.name}`);
      return event;
    },
    async trackPageView(pageView) {
      await putJson(
        `pageviews:${pageView.sessionId}:${pageView.timestamp}`,
        pageView,
        pageViewsTtlSeconds,
      );
      await touchSession({
        sessionId: pageView.sessionId,
        userId: pageView.userId,
        timestamp: pageView.timestamp,
      });
      const aggregateKey = `aggregates:pageviews:${encodeURIComponent(pageView.url)}`;
      const current = await getJson<StoredPageViewAggregate>(aggregateKey);
      const visitorId = pageView.userId ?? pageView.sessionId;
      const visitorIds = new Set(current?.visitorIds ?? []);
      visitorIds.add(visitorId);
      await putJson(
        aggregateKey,
        {
          url: pageView.url,
          views: (current?.views ?? 0) + 1,
          uniqueVisitors: visitorIds.size,
          lastViewedAt: Math.max(
            current?.lastViewedAt ?? pageView.timestamp,
            pageView.timestamp,
          ),
          visitorIds: [...visitorIds].sort(),
        } satisfies StoredPageViewAggregate,
        options.pageViewAggregatesTtlSeconds,
      );
      await this.incrementCounter("pageviews");
      return pageView;
    },
    async identifyUser(userId, traits) {
      await putJson(`users:${userId}`, {
        userId,
        traits,
        updatedAt: Date.now(),
      });
    },
    async listEvents(limit = 100) {
      const result = await options.namespace.list({
        prefix: `${prefix}:events:`,
        limit,
      });
      const events = await Promise.all(
        result.keys.map(async (key) => {
          const value = await options.namespace.get(key.name);
          return value ? (JSON.parse(value) as AnalyticsEvent) : null;
        }),
      );
      return events.filter((event): event is AnalyticsEvent => event !== null);
    },
    async getEvent(id) {
      return getJson<AnalyticsEvent>(`events:${id}`);
    },
    async deleteEvent(id) {
      const key = `events:${id}`;
      const existed = (await getJson<AnalyticsEvent>(key)) !== null;
      await options.namespace.delete(`${prefix}:${key}`);
      return existed;
    },
    async putConsentState(userId, consent) {
      await putJson(`consent:${userId}`, consent);
    },
    async getConsentState(userId) {
      return getJson<ConsentSettings>(`consent:${userId}`);
    },
    async upsertSession(session) {
      const existing = await getJson<CloudflareKVSessionRecord>(
        `sessions:${session.sessionId}`,
      );
      const next: CloudflareKVSessionRecord = {
        ...existing,
        ...session,
        startedAt: existing?.startedAt ?? session.startedAt,
        lastSeenAt: Math.max(existing?.lastSeenAt ?? 0, session.lastSeenAt),
      };
      await putJson(`sessions:${session.sessionId}`, next, sessionsTtlSeconds);
      return next;
    },
    async getSession(sessionId) {
      return getJson<CloudflareKVSessionRecord>(`sessions:${sessionId}`);
    },
    async incrementCounter(name, delta = 1) {
      const current = await getJson<CloudflareKVCounterRecord>(
        `counters:${name}`,
      );
      const next = {
        name,
        value: (current?.value ?? 0) + delta,
        updatedAt: Date.now(),
      } satisfies CloudflareKVCounterRecord;
      await putJson(`counters:${name}`, next, options.countersTtlSeconds);
      return next;
    },
    async getCounter(name) {
      return getJson<CloudflareKVCounterRecord>(`counters:${name}`);
    },
    async getPageViewAggregate(url) {
      const aggregate = await getJson<StoredPageViewAggregate>(
        `aggregates:pageviews:${encodeURIComponent(url)}`,
      );
      return aggregate ? publicPageAggregate(aggregate) : null;
    },
    async listPageViewAggregates(limit = 100) {
      const aggregates = await listJson<StoredPageViewAggregate>(
        "aggregates:pageviews:",
        limit,
      );
      return aggregates
        .map(publicPageAggregate)
        .sort((left, right) => right.views - left.views);
    },
    async putAggregateSnapshot(snapshot) {
      const snapshotTtlSeconds = options.aggregateSnapshotsTtlSeconds;
      await putJson(
        `snapshots:${snapshot.kind}:${snapshot.id}`,
        snapshot,
        snapshotTtlSeconds,
      );
      await putJson(
        `snapshot-index:${snapshot.id}`,
        {
          id: snapshot.id,
          kind: snapshot.kind,
        } satisfies AggregateSnapshotIndexEntry,
        snapshotTtlSeconds,
      );
      return snapshot;
    },
    async getAggregateSnapshot<TData = unknown>(id: string) {
      const indexEntry = await getJson<AggregateSnapshotIndexEntry>(
        `snapshot-index:${id}`,
      );
      return indexEntry
        ? getJson<CloudflareKVAggregateSnapshot<TData>>(
            `snapshots:${indexEntry.kind}:${id}`,
          )
        : null;
    },
    async listAggregateSnapshots(kind, limit = 100) {
      const snapshotPrefix = kind ? `snapshots:${kind}:` : "snapshots:";
      return listJson<CloudflareKVAggregateSnapshot<unknown>>(
        snapshotPrefix,
        limit,
      );
    },
    async deleteUserData(userId) {
      await options.namespace.delete(`${prefix}:users:${userId}`);
      await options.namespace.delete(`${prefix}:consent:${userId}`);
      const eventResult = await options.namespace.list({
        prefix: `${prefix}:events:`,
      });
      for (const key of eventResult.keys) {
        const raw = await options.namespace.get(key.name);
        if (raw) {
          const event = JSON.parse(raw) as AnalyticsEvent;
          if (event.userId === userId) {
            await options.namespace.delete(key.name);
          }
        }
      }
      const pageViewResult = await options.namespace.list({
        prefix: `${prefix}:pageviews:`,
      });
      for (const key of pageViewResult.keys) {
        const raw = await options.namespace.get(key.name);
        if (raw) {
          const pageView = JSON.parse(raw) as PageView;
          if (pageView.userId === userId) {
            await options.namespace.delete(key.name);
          }
        }
      }
      const sessionResult = await options.namespace.list({
        prefix: `${prefix}:sessions:`,
      });
      for (const key of sessionResult.keys) {
        const raw = await options.namespace.get(key.name);
        if (raw) {
          const session = JSON.parse(raw) as CloudflareKVSessionRecord;
          if (session.userId === userId) {
            await options.namespace.delete(key.name);
          }
        }
      }
    },
    unsupportedRelationalQuery(operation) {
      throw new AdapterUnsupportedError({
        message: `Cloudflare KV analytics does not support relational operation: ${operation}.`,
        context: { provider: "cloudflareKV", operation },
      });
    },
  };
}
